import { Category, Item, MenuDiscount, MenuDiscountScheduleDay, Modifier, ModifierList, Variation } from '../models/catalog.model';
import { Restaurant, WorkTime } from '../models/restaurant.model';

export const dataToCategory = (
  data: any,
  restaurant: Restaurant,
  unavailableItems?: Item[],
  pickupAt?: string | null,
): Category | null => {
  const items =
    data?.items
      ?.map((item: any) => dataToItem(item, restaurant, unavailableItems, pickupAt, data))
      ?.filter((item: Item | null) => item !== null) ?? [];

  const subcategories =
    data?.subcategories
      ?.map((category: any) => dataToCategory(category, restaurant, unavailableItems, pickupAt))
      ?.filter((category: Category | null) => category !== null) ?? [];

  if (items.length === 0 && subcategories.length === 0) {
    return null;
  }

  return new Category(
    data?.id,
    data?.name,
    data?.image_url,
    items as Item[],
    subcategories as Category[],
    restaurant.categoriesCollapsable,
    restaurant.categoriesCollapsable,
    false,
    data?.parent_category_id,
    dataToMenuDiscount(data?.discount),
  );
};

export const dataToMenuDiscount = (data: any): MenuDiscount | null =>
  data
    ? new MenuDiscount(
        data.id,
        data.name,
        data.discount_type,
        data.percentage != null ? Number(data.percentage) : null,
        data.amount_money ?? null,
        data.quantity != null ? Number(data.quantity) : null,
        Boolean(data.buy_one_get_one),
        data.apply_quantity != null ? Number(data.apply_quantity) : null,
        data.valid_from ?? null,
        data.valid_until ?? null,
        (data.schedule ?? []).map((day: any) => new MenuDiscountScheduleDay(day.day_type, day.start_time, day.end_time)),
      )
    : null;

export const dataToItem = (
  data: any,
  restaurant: Restaurant,
  unavailableItems?: Item[],
  pickupAt?: string | null,
  category: Category | null = null,
): Item | null => {
  const variationsAll: Variation[] = (data?.variations ?? []).map((v: any) => dataToVariation(v));
  const variationsAvailable: Variation[] = variationsAll.filter((v: Variation) =>
    isNowAvailable(v, restaurant, pickupAt),
  );

  if (variationsAvailable.length === 0) {
    const canCollect =
      !restaurant.hideItemsOutsideDailyAvailability &&
      restaurant.showNotCurrentlySoldCategory &&
      Array.isArray(unavailableItems);

    if (canCollect) {
      const item = new Item(
        data?.id,
        category?.id,
        data?.name,
        data?.description,
        data?.image_url,
        variationsAll,
        restaurant.locationId,
        0,
        true,
        dataToMenuDiscount(data?.discount),
      );
      (unavailableItems as Item[]).push(item);
    }
    return null;
  }

  return new Item(
    data?.id,
    category?.id,
    data?.name,
    data?.description,
    data?.image_url,
    variationsAvailable,
    restaurant.locationId,
    0,
    true,
    dataToMenuDiscount(data?.discount),
  );
};

export const dataToVariation = (data: any): Variation => {
  const normalizedAvailability =
    data?.daily_availability_enabled
      ? getDailyAvailability(data?.daily_availability)
      : [];

  return new Variation(
    data?.id,
    data?.category_id,
    data?.name,
    data?.price_money,
    data?.opening_lead_time,
    data?.closing_lead_time,
    data?.pickup_lead_time,
    data?.allow_custom_note,
    normalizedAvailability,
    data?.daily_availability_enabled,
    data?.fulfillments,
    data?.available_from ? new Date(data.available_from) : null,
    data?.available_until ? new Date(data.available_until) : null,
    data?.fulfillment_restriction_enabled,
    data?.track_inventory,
    data?.sold_out,
    (data?.modifier_lists ?? []).map((list: any) => dataToModifierList(list)),
    data?.nutrition_facts,
    data?.dietary_labels,
    data?.contains_alcohol,
    null,
    null,
    dataToMenuDiscount(data?.discount),
  );
};

export const dataToModifierList = (data: any): ModifierList =>
  new ModifierList(
    data?.id,
    data?.allow_quantities,
    data?.max_selected_modifiers,
    data?.min_selected_modifiers,
    data?.name,
    (data?.modifiers ?? []).map((modifier: any) => dataToModifier(modifier)),
  );

export const dataToModifier = (data: any): Modifier =>
  new Modifier(
    data?.id,
    data?.name,
    data?.price_money,
    data?.hidden_online,
    data?.on_by_default,
    data?.sold_out,
    false,
    0,
  );

export class DailyAvailability {
  constructor(
    public enabled: boolean = true,
    public value: number = 0,
    public startTime: WorkTime = new WorkTime(),
    public endTime: WorkTime = new WorkTime(),
  ) {}
}

export const getDailyAvailability = (data: any[] | null | undefined): DailyAvailability[] => {
  const byValue = new Map<number, any>();
  if (Array.isArray(data)) {
    for (const d of data) {
      const v = Number(d?.value);
      if (Number.isFinite(v)) byValue.set(v, d);
    }
  }

  const result: DailyAvailability[] = [];
  for (let v = 0; v < 7; v++) {
    const src = byValue.get(v);
    result.push(
      src
        ? dataToDailyAvailability(src)
        : new DailyAvailability(false, v, new WorkTime(), new WorkTime()),
    );
  }
  return result;
};

export const dataToDailyAvailability = (data: any): DailyAvailability =>
  new DailyAvailability(
    Boolean(data?.enabled),
    Number(data?.value ?? 0),
    toWorkTime(data?.start_time),
    toWorkTime(data?.end_time),
  );

export function isNowAvailable(variation: Variation, restaurant: Restaurant, pickupAt?: string | null): boolean {
  const strict = restaurant.hideItemsOutsideDailyAvailability || restaurant.showNotCurrentlySoldCategory;
  if (!strict) return true;

  const availability = variation.daily_availability as DailyAvailability[] | undefined | null;

  if (!variation.daily_availability_enabled) return true;

  if (!availability || availability.length === 0) return true;

  // If pickupAt is provided, use it; otherwise use current time
  const { dowSunday0, dowMonday0, minutesNow } = pickupAt
    ? getMinutesForPickupTime(pickupAt, restaurant)
    : getNowMinutesForTz(restaurant);
  let cur = availability.find(d => d?.value === dowSunday0);
  if (!cur) cur = availability.find(d => d?.value === dowMonday0);
  if (!cur?.enabled) return false;

  const startMinutes = toMinutes12h(cur.startTime);
  const endMinutes = toMinutes12h(cur.endTime);

  if (startMinutes === 0 && endMinutes === 1439) {
    return true;
  }

  if (startMinutes <= endMinutes) {
    return startMinutes <= minutesNow && minutesNow <= endMinutes;
  } else {
    return minutesNow >= startMinutes || minutesNow <= endMinutes;
  }
}

function toWorkTime(src: any): WorkTime {
  if (!src || typeof src !== 'object') return new WorkTime();
  const wt = new WorkTime();
  wt.time = {
    hour: clampInt(safeInt(src?.time?.hour, 0), 0, 12),
    minute: clampInt(safeInt(src?.time?.minute, 0), 0, 59),
  };
  wt.meridian = Boolean(src?.meridian);
  return wt;
}

function toMinutes12h(time: WorkTime): number {
  const meridianAdd = time.meridian ? 0 : 12;
  const hour12 = time.time?.hour === 12 ? 0 : (time.time?.hour ?? 0);
  const minute = time.time?.minute ?? 0;
  return (hour12 + meridianAdd) * 60 + minute;
}

function getNowMinutesForTz(_: Restaurant): { dowSunday0: number; dowMonday0: number; minutesNow: number } {
  const base = new Date();

  const dowSunday0 = base.getDay();
  const dowMonday0 = (dowSunday0 + 6) % 7;
  const minutesNow = base.getHours() * 60 + base.getMinutes();

  return { dowSunday0, dowMonday0, minutesNow };
}

function getMinutesForPickupTime(pickupAt: string, _: Restaurant): { dowSunday0: number; dowMonday0: number; minutesNow: number } {
  const pickupDate = new Date(pickupAt);

  const dowSunday0 = pickupDate.getDay();
  const dowMonday0 = (dowSunday0 + 6) % 7;
  const minutesNow = pickupDate.getHours() * 60 + pickupDate.getMinutes();

  return { dowSunday0, dowMonday0, minutesNow };
}

function safeInt(v: any, def: number): number {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
}
function clampInt(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Math.trunc(n)));
}
